<script setup>
import { defineProps, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  formatCheck: Boolean,
  sizeCheck: Boolean,
  often: Boolean,
  serverBusy: Boolean,
  routeName: {
    type: String,
    default: '' 
  }
})

const emptyTextMap = computed(() => {
  // value 为 [第一行文本, 第二行文本]
  return {
    qrCode: [t('home.qrEmpty'), t('home.selectQr')],
    ocrVehiclePlate: [t('home.plateEmpty'), t('home.selectPlate')],
    vehicleDetection: [t('home.vehicleEmpty'), t('home.selectVehicle')],
    bodyHandKeypoint: [t('home.handleEmpty'), t('home.selectHand')],
    bodyHelmetDetection: [t('home.helmetEmpty'), t('home.selectHelmet')],
    detectionFire: [t('home.fireEmpty'), t('home.selectFire')],
    bodyGestureRecognition: [t('home.gestureEmpty'), t('home.selectGesture')],
    bodyReflectiveVestDetection: [t('home.vestEmpty'), t('home.selectVest')],
    bodyDetectionSmoking: [t('home.smokeEmpty'), t('home.selectSmoke')],
    classificationVehicleType: [t('home.vehicleEmpty'), t('home.selectVehicle')],
    classificationGeneral: [t('home.thingEmpty'), t('home.selectThing')],
    ocrWaterMeter: [t('home.thingEmpty'), t('home.selectThing')],
    imageTechColoring: [t('home.techEmpty'), t('home.selectTech')],
    classificationLandmark: ['未检测到地标', '请重新选择，确保地标正对画面'],
    classificationAnimal: [t('home.animalEmpty'), t('home.selectAnimal')],
    classificationDishes: [t('home.dishesEmpty'), t('home.selectDishes')],
    classificationVegetables: [t('home.vegetablesEmpty'), t('home.selectVegetables')],
    // 批量匹配多个 routeName 对应同一套文本
    bodyCommon: [t('home.bodyEmpty'), t('home.selectBody')]
  }
})

const currentEmptyText = computed(() => {
  const { routeName } = props
  // body 相关路由
  const bodyCommonRoutes = ['bodyHeadShoulderDetection', 'bodyAttribute', 'bodyKeypoint', 'bodyDetection', 'bodyHumanSegmentation']
  
  // 优先匹配批量路由
  if (bodyCommonRoutes.includes(routeName)) {
    return emptyTextMap.value.bodyCommon
  }
  
  // 匹配单个路由，无匹配则返回默认文本
  return emptyTextMap.value[routeName] || [t('homepage.noFaceDetected'), t('homepage.pleaseSelectAgain')]
})

// 判断当前是否显示基础提示
const showBaseTip = computed(() => {
  const { formatCheck, sizeCheck, often, serverBusy } = props
  return !formatCheck && !sizeCheck && !often && !serverBusy
})
</script>

<template>
  <a-empty>
    <template #description>
      <span class="empty-item">
        <!-- 基础提示：格式/大小/频率/服务器繁忙 -->
        <span v-if="formatCheck">{{ t('homepage.invalidImageFormat') }}</span>
        <span v-else-if="sizeCheck">
          <div class="empty-tip-line">{{ t('homepage.imageTooLarge') }}</div>
          <div class="empty-tip-line">{{ t('homepage.imageSizeLimit') }}</div>
        </span>
        <span v-else-if="often">
          <div class="empty-tip-line">{{ t('homepage.tooFrequent') }}</div>
          <div class="empty-tip-line">{{ t('homepage.pleaseTryAgain') }}</div>
        </span>
        <span v-else-if="serverBusy">
          <div class="empty-tip-line">{{ t('messages.serverBusy') }}</div>
        </span>
        
        <!-- 路由对应的个性化提示 -->
        <div v-if="showBaseTip" class="empty-route-tips">
          <div class="empty-tip-line">{{ currentEmptyText[0] }}</div>
          <div class="empty-tip-line">{{ currentEmptyText[1] }}</div>
        </div>
      </span>
    </template>
  </a-empty>
</template>

<style lang="scss" scoped>
.empty-item {
  color: #666;
  font-size: 16px;

  .empty-tip-line {
    text-wrap: nowrap;
    margin: 4px 0; 
  }

  .empty-route-tips {
    margin: 0;
    padding: 0;
  }
}
</style>
